import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BarChart3, Download, FileSpreadsheet } from "lucide-react";

const BRANDS = ["Wellcore", "Dynamite", "YouWeFit", "Okami", "OSMO"];
const THRESHOLD = 62;

const fmtMoney = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`);
const fmtPct = (v) => (v == null ? "—" : `${Number(v).toFixed(2)}%`);
const currentMonth = () => new Date().toISOString().slice(0, 7);

export default function MonthlySalesAnalysis() {
  const [month, setMonth] = useState(currentMonth());
  const [sellerId, setSellerId] = useState("all");
  const [brand, setBrand] = useState("all");
  const [minDiscount, setMinDiscount] = useState("");
  const [sellers, setSellers] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/sellers`, { withCredentials: true })
      .then((r) => setSellers(r.data || []))
      .catch(() => {});
  }, []);

  const buildParams = () => {
    const p = { month };
    if (sellerId !== "all") p.seller_id = sellerId;
    if (brand !== "all") p.brand = brand;
    if (minDiscount !== "" && !isNaN(Number(minDiscount))) p.min_discount = Number(minDiscount);
    return p;
  };

  const generate = async () => {
    if (!month) { toast.error("Please select a month"); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/monthly-sales`, { params: buildParams(), withCredentials: true });
      setReport(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/analytics/monthly-sales/export`, {
        params: buildParams(), withCredentials: true, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `Monthly_Sales_${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to export Excel");
    } finally {
      setExporting(false);
    }
  };

  const redIf = (v) => (v != null && v > THRESHOLD ? "bg-red-100 text-red-700 font-semibold rounded px-1.5 py-0.5" : "");
  const rows = report?.rows || [];

  return (
    <div className="space-y-6" data-testid="monthly-sales-page">
      <div className="flex items-center gap-3">
        <BarChart3 className="h-6 w-6 text-[#002FA7]" />
        <div>
          <h1 className="text-2xl font-bold text-[#0F0F0F]">Monthly Sales Analysis</h1>
          <p className="text-sm text-muted-foreground">Brand-wise monthly invoice report — generated automatically from invoice data.</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Month *</label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="msa-month" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Seller</label>
              <Select value={sellerId} onValueChange={setSellerId}>
                <SelectTrigger data-testid="msa-seller"><SelectValue placeholder="All Sellers" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sellers</SelectItem>
                  {sellers.map((s) => <SelectItem key={s.id} value={s.id}>{s.seller_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Brand</label>
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger data-testid="msa-brand"><SelectValue placeholder="All Brands" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {BRANDS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Min Overall Discount %</label>
              <Input type="number" placeholder="e.g. 62" value={minDiscount} onChange={(e) => setMinDiscount(e.target.value)} data-testid="msa-min-discount" />
            </div>
            <div className="flex gap-2">
              <Button onClick={generate} disabled={loading} className="bg-[#002FA7] hover:bg-[#002685] text-white flex-1" data-testid="msa-generate-btn">
                {loading ? "Generating…" : "Generate Report"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {report && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              {report.count} invoice{report.count === 1 ? "" : "s"} · {report.month}
              {report.exception_count > 0 && (
                <span className="ml-2 text-xs font-medium text-red-600" data-testid="msa-exception-count">
                  ({report.exception_count} over {THRESHOLD}% discount)
                </span>
              )}
            </CardTitle>
            <Button variant="outline" onClick={exportExcel} disabled={exporting || rows.length === 0} data-testid="msa-export-btn">
              {exporting ? <Download className="h-4 w-4 mr-1 animate-pulse" /> : <FileSpreadsheet className="h-4 w-4 mr-1" />}
              Export to Excel
            </Button>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="msa-empty">No invoices found for the selected filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice Date</TableHead>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Seller</TableHead>
                      <TableHead className="text-right">Total Invoice</TableHead>
                      <TableHead className="text-right">Total MRP</TableHead>
                      <TableHead className="text-right">Overall Disc %</TableHead>
                      {BRANDS.map((b) => (
                        <TableHead key={b} colSpan={3} className="text-center border-l bg-slate-50">{b}</TableHead>
                      ))}
                      <TableHead>Remarks</TableHead>
                    </TableRow>
                    <TableRow>
                      <TableHead colSpan={6}></TableHead>
                      {BRANDS.map((b) => (
                        <React.Fragment key={b}>
                          <TableHead className="text-right border-l text-[11px]">Invoice</TableHead>
                          <TableHead className="text-right text-[11px]">MRP</TableHead>
                          <TableHead className="text-right text-[11px]">Disc %</TableHead>
                        </React.Fragment>
                      ))}
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r, idx) => (
                      <TableRow key={r.quotation_id || idx} data-testid={`msa-row-${idx}`}>
                        <TableCell>{r.invoice_date || "—"}</TableCell>
                        <TableCell className="font-medium">{r.invoice_number || "—"}</TableCell>
                        <TableCell>{r.seller_name}</TableCell>
                        <TableCell className="text-right">{fmtMoney(r.total_invoice_value)}</TableCell>
                        <TableCell className="text-right">{fmtMoney(r.total_mrp_value)}</TableCell>
                        <TableCell className="text-right"><span className={redIf(r.overall_discount)} data-testid={`msa-overall-disc-${idx}`}>{fmtPct(r.overall_discount)}</span></TableCell>
                        {BRANDS.map((b) => {
                          const bd = r.brands[b] || {};
                          return (
                            <React.Fragment key={b}>
                              <TableCell className="text-right border-l">{bd.mrp_value ? fmtMoney(bd.invoice_value) : "—"}</TableCell>
                              <TableCell className="text-right">{bd.mrp_value ? fmtMoney(bd.mrp_value) : "—"}</TableCell>
                              <TableCell className="text-right"><span className={redIf(bd.discount)}>{fmtPct(bd.discount)}</span></TableCell>
                            </React.Fragment>
                          );
                        })}
                        <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">{r.remarks || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
